import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ChevronLeft, FileCheck, Upload, X, RefreshCw, Sparkles, AlertTriangle, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { calculateBundlePrice } from '@/data/mockData';
import PaymentModal from '@/components/PaymentModal';

const BundleResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fileCount = 0, files = [] } = location.state || {};
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [documentsToSkip, setDocumentsToSkip] = useState([]);
  const [documentsToReupload, setDocumentsToReupload] = useState([]);

  useEffect(() => {
    if (fileCount === 0) {
      navigate('/select-country');
    }
  }, [fileCount, navigate]);

  // Generate mock results for each document
  const documentResults = files.map((file, index) => {
    const scores = [92, 78, 85, 65, 88, 72, 95, 81, 76, 90, 84, 79, 87];
    const score = scores[index % scores.length];
    
    return {
      id: index,
      name: file.name || `Document ${index + 1}`,
      score,
      status: score >= 85 ? 'ready' : score >= 70 ? 'warning' : 'reject-risk',
      criticalIssues: score < 70 ? 1 : 0,
      warnings: score < 85 ? Math.floor(Math.random() * 2) + 1 : 0,
      passed: Math.floor(Math.random() * 3) + 2,
    };
  });

  const overallScore = Math.round(
    documentResults.reduce((sum, doc) => sum + doc.score, 0) / documentResults.length
  );

  const totalCritical = documentResults.reduce((sum, doc) => sum + doc.criticalIssues, 0);
  const totalWarnings = documentResults.reduce((sum, doc) => sum + doc.warnings, 0);

  const bundlePricing = calculateBundlePrice(fileCount);

  const handleSkipDocument = (docId) => {
    if (documentsToSkip.includes(docId)) {
      setDocumentsToSkip(documentsToSkip.filter(id => id !== docId));
    } else {
      setDocumentsToSkip([...documentsToSkip, docId]);
    }
  };

  const handleReupload = (docId) => {
    setDocumentsToReupload([...documentsToReupload, docId]);
    // In real app, would open file picker for this specific document
    alert(`Re-upload functionality for ${documentResults[docId].name} would open here`);
  };

  const handleUnlock = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setIsPremium(true);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'ready': return <CheckCircle2 className="w-5 h-5 text-verify" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-warning" />;
      case 'reject-risk': return <XCircle className="w-5 h-5 text-destructive" />;
      default: return <FileCheck className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'ready': return 'border-verify/30 bg-verify/5';
      case 'warning': return 'border-warning/30 bg-warning/5';
      case 'reject-risk': return 'border-destructive/30 bg-destructive/5';
      default: return 'border-slate-200 bg-white';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/select-country')}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-trust" />
              <h1 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900">Bundle Analysis Complete</h1>
            </div>
            <p className="font-public text-lg text-slate-600">Here's the analysis for all {fileCount} documents</p>
          </div>

          {/* Overall Summary Card */}
          <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-sm font-public text-slate-600 mb-2">Overall Score</p>
                <p className="text-5xl font-outfit font-bold text-trust">{overallScore}</p>
                <p className="text-sm text-slate-500 mt-1">/100</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-public text-slate-600 mb-2">Documents</p>
                <p className="text-5xl font-outfit font-bold text-slate-900">{fileCount}</p>
                <p className="text-sm text-slate-500 mt-1">analyzed</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-public text-slate-600 mb-2">Critical Issues</p>
                <p className="text-5xl font-outfit font-bold text-destructive">{totalCritical}</p>
                <p className="text-sm text-slate-500 mt-1">found</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-public text-slate-600 mb-2">Warnings</p>
                <p className="text-5xl font-outfit font-bold text-warning">{totalWarnings}</p>
                <p className="text-sm text-slate-500 mt-1">found</p>
              </div>
            </div>
          </div>

          {/* Individual Document Results */}
          <div className="space-y-4 mb-8">
            <h2 className="font-outfit font-bold text-2xl text-slate-900">Individual Results</h2>
            {documentResults.map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`border-2 rounded-lg p-6 ${getStatusColor(doc.status)} ${documentsToSkip.includes(doc.id) ? 'opacity-50' : ''}`}
                data-testid={`document-result-${doc.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {getStatusIcon(doc.status)}
                      <h3 className="font-public font-semibold text-lg text-slate-900">{doc.name}</h3>
                      {documentsToSkip.includes(doc.id) && (
                        <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs font-mono rounded">SKIPPED</span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-slate-600">Score</p>
                        <p className="text-2xl font-outfit font-bold text-trust">{doc.score}/100</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Critical</p>
                        <p className="text-2xl font-outfit font-bold text-destructive">{doc.criticalIssues}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Warnings</p>
                        <p className="text-2xl font-outfit font-bold text-warning">{doc.warnings}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Passed</p>
                        <p className="text-2xl font-outfit font-bold text-verify">{doc.passed}</p>
                      </div>
                    </div>

                    {/* Preview of issues (locked if not premium) */}
                    {!isPremium ? (
                      <div className="bg-slate-100 rounded p-3 text-sm text-slate-600">
                        <Lock className="w-4 h-4 inline mr-2" />
                        Unlock full report to see detailed issues and fix instructions
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {doc.criticalIssues > 0 && (
                          <div className="flex items-start gap-2 text-sm">
                            <XCircle className="w-4 h-4 text-destructive mt-0.5" />
                            <span className="text-slate-700">Missing apostille certification</span>
                          </div>
                        )}
                        {doc.warnings > 0 && (
                          <div className="flex items-start gap-2 text-sm">
                            <AlertTriangle className="w-4 h-4 text-warning mt-0.5" />
                            <span className="text-slate-700">Minor formatting inconsistency detected</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleReupload(doc.id)}
                      className="p-2 hover:bg-trust/10 rounded-md transition-colors group"
                      title="Re-upload this document"
                      data-testid={`reupload-${doc.id}`}
                    >
                      <RefreshCw className="w-5 h-5 text-trust" />
                    </button>
                    <button
                      onClick={() => handleSkipDocument(doc.id)}
                      className="p-2 hover:bg-slate-200 rounded-md transition-colors"
                      title={documentsToSkip.includes(doc.id) ? 'Include document' : 'Skip document'}
                      data-testid={`skip-${doc.id}`}
                    >
                      <X className={`w-5 h-5 ${documentsToSkip.includes(doc.id) ? 'text-slate-400' : 'text-slate-600'}`} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Unlock Premium */}
          {!isPremium && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-trust/5 to-verify/5 border-2 border-trust/20 rounded-lg p-8 text-center mb-8"
            >
              <Sparkles className="w-12 h-12 text-trust mx-auto mb-4" />
              <h3 className="font-outfit font-bold text-2xl text-slate-900 mb-2">Unlock Full Bundle Report</h3>
              <p className="font-public text-slate-600 mb-6">
                Get detailed fix instructions for all {fileCount - documentsToSkip.length} documents
              </p>
              <div className="flex items-center justify-center gap-4 mb-6">
                <span className="text-4xl font-outfit font-bold text-trust">${bundlePricing.finalPrice}</span>
                <span className="text-xl text-slate-400 line-through">${bundlePricing.totalWithoutDiscount}</span>
                <span className="px-3 py-1 bg-verify text-verify-foreground rounded-md font-mono text-sm">Save ${bundlePricing.savings}</span>
              </div>
              <button
                onClick={handleUnlock}
                className="bg-trust text-trust-foreground px-8 py-4 rounded-md font-public font-semibold text-lg hover:bg-trust/90 active:scale-95 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2"
                data-testid="unlock-bundle-button"
              >
                <Sparkles className="w-5 h-5" />
                Unlock Full Report
              </button>
            </motion.div>
          )}

          {/* Upload More */}
          <div className="text-center">
            <button
              onClick={() => navigate('/select-country')}
              className="bg-white border-2 border-trust text-trust px-8 py-4 rounded-md font-public font-semibold text-lg hover:bg-trust hover:text-trust-foreground active:scale-95 transition-all shadow-sm inline-flex items-center gap-2"
              data-testid="check-more-button"
            >
              <Upload className="w-5 h-5" />
              Check More Documents
            </button>
          </div>
        </motion.div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          bundleCount={fileCount}
        />
      )}
    </div>
  );
};

export default BundleResultsPage;
